import { useTranslation } from "react-i18next";
import "./application_tracking_widget.scss";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import classNames from "classnames";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { Button } from "./button";
import { Body } from "./typography";
import ApplicationStages from "./application_stages";
import { SnowplowEvent, useSnowplowTracker } from "~/hooks/use_snowplow_tracker";
import serverAssetUrl from "~/utils/asset_url.server";
import { serverOnly$ } from "vite-env-only/macros";

const DESIGN_VARIANTS = ["design_a", "design_b", "design_c"] as const;
type DesignVariant = (typeof DESIGN_VARIANTS)[number];
const DEFAULT_DESIGN_VARIANT: DesignVariant = "design_a";

const randomDesignVariant = (): DesignVariant =>
  DESIGN_VARIANTS[Math.floor(Math.random() * DESIGN_VARIANTS.length)];

const variantEventData = (trackedVariant?: DesignVariant) =>
  trackedVariant ? { variant: trackedVariant } : {};

const variantUrlParam = (trackedVariant?: DesignVariant) =>
  trackedVariant ? `&variant=${trackedVariant}` : "";

const ApplicationTrackingWidget = () => {
  const { t } = useTranslation("job_post");
  const [jobSeekersUrl, setJobSeekersUrl] = useState("");
  const [designVariant, setDesignVariant] = useState<DesignVariant>();
  const [variantsEnabled, setVariantsEnabled] = useState(false);
  const { preview_mode, button_shape } = useBoardConfiguration();
  const { trackEvent } = useSnowplowTracker();
  const assetUrl = serverOnly$(serverAssetUrl) || window.ENV.ASSET_URL;
  const trackedVariant = variantsEnabled ? designVariant : undefined;

  useEffectOnce(() => {
    const enabled = window.ENV.TRACK_STATUS_DESIGN_VARIANTS;
    setVariantsEnabled(enabled);
    setDesignVariant(enabled ? randomDesignVariant() : DEFAULT_DESIGN_VARIANT);
  });

  useEffect(() => {
    designVariant &&
      !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdShown, {
        type: "track_your_status",
        ...variantEventData(trackedVariant),
      });
  });

  useEffect(() => {
    setJobSeekersUrl(
      `${window.ENV.JOB_SEEKERS_URL}/users/sign_in?source=track_your_status${variantUrlParam(
        trackedVariant
      )}`
    );
  }, [trackedVariant]);

  const trackClick = () => {
    !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdClicked, {
        type: "track_your_status",
        ...variantEventData(trackedVariant),
      });
  };

  return (
    <div
      className={classNames("application_tracking_widget", {
        [`application_tracking_widget--${designVariant}`]: !!designVariant,
      })}
    >
      <div className="application_tracking_widget__icon">
        <img src={`${assetUrl}/greenhouse_white_g.svg`} alt="MyGreenhouse Application Tracking" />
      </div>
      <div className="application_tracking_widget__content">
        <div className="application_tracking_widget__content__header">
          <Body medium>{t("application_tracking_widget.header")}</Body>
          <Body>{t("application_tracking_widget.body")}</Body>
        </div>
        {designVariant === "design_c" && <ApplicationStages />}
        <Button
          secondary
          link
          href={preview_mode ? undefined : jobSeekersUrl}
          target="_blank"
          shape={button_shape}
          onClick={trackClick}
        >
          {t("application_tracking_widget.create_account")}
        </Button>
      </div>
    </div>
  );
};

export default ApplicationTrackingWidget;
